import React from "react";
import { Reorder } from "framer-motion";
import { GripVertical, Pin, X } from "lucide-react";
import HoverRevealScrollbarWrapper from "Components/HoverRevealScrollbarWrapper/HoverRevealScrollbarWrapper";
import { Tooltip } from "react-tooltip";

const ShortcutManagerPopup = ({
  isOpen,
  shortcuts,
  pinnedShortcutKeys,
  onClose,
  onTogglePin,
  onSelectAll,
  onDeselectAll,
  onReorder,
  renderIcon,
}) => {
  if (!isOpen) return null;
  const orderedShortcuts = [
    ...shortcuts.filter((tab) => pinnedShortcutKeys.includes(tab.shortcutKey)),
    ...shortcuts.filter((tab) => !pinnedShortcutKeys.includes(tab.shortcutKey)),
  ];
  const allSelected = shortcuts.length > 0 && pinnedShortcutKeys.length === shortcuts.length;
  const noneSelected = pinnedShortcutKeys.length === 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/25 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] overflow-hidden rounded-xl border border-[#EBEBEF] bg-white shadow-[0_10px_40px_rgba(0,0,0,.12)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-[#EBEBEF] px-4 py-[14px]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Manage Shortcuts</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center bg-transparent text-gray-500 hover:text-gray-700"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-1 flex items-center justify-end gap-3 text-[11px]">
            <button
              type="button"
              onClick={onSelectAll}
              disabled={allSelected}
              className={`bg-transparent ${allSelected ? "cursor-not-allowed text-slate-300" : "text-primary hover:text-[#4F3EF7]"}`}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={onDeselectAll}
              disabled={noneSelected}
              className={`bg-transparent ${noneSelected ? "cursor-not-allowed text-slate-300" : "text-primary hover:text-[#4F3EF7]"}`}
            >
              Deselect all
            </button>
          </div>
        </div>

        <HoverRevealScrollbarWrapper className="max-h-[62vh]">
          <div data-hover-scrollable="true" className="max-h-[62vh] overflow-y-auto overflow-x-hidden p-3">
            <Reorder.Group
              axis="y"
              values={orderedShortcuts}
              onReorder={(nextList) => onReorder(nextList.map((item) => item.shortcutKey))}
              className="grid gap-2"
            >
              {orderedShortcuts.map((tab) => (
                <Reorder.Item
                  key={`manage-${tab.shortcutKey}`}
                  value={tab}
                  className="flex items-center gap-2 rounded-lg border border-[#EBEBEF] bg-white px-[10px] py-2 text-gridHeader"
                  transition={{ type: "spring", stiffness: 520, damping: 42 }}
                >
                  <span className="inline-flex cursor-grab items-center justify-center text-slate-400">
                    <GripVertical size={14} />
                  </span>
                  <span className="inline-flex h-[15px] w-[15px] shrink-0">
                    {renderIcon(tab)}
                  </span>
                  <span className="flex-1 text-[12.5px]">{tab.title}</span>
                  {(() => {
                    const isPinned = pinnedShortcutKeys.includes(tab.shortcutKey);
                    return (
                      <button
                        type="button"
                        onClick={() => onTogglePin(tab.shortcutKey)}
                        data-tooltip-id={`shortcut-manager-pin-${tab.shortcutKey}`}
                        data-tooltip-content={`${isPinned ? "Unpin" : "Pin"} ${tab.title}`}
                        className={`inline-flex items-center justify-center rounded-md bg-transparent p-0.5 ${isPinned ? "text-primary" : "text-slate-400"}`}
                      >
                        <Pin size={14} fill={isPinned ? "currentColor" : "none"} />
                      </button>
                    );
                  })()}
                  <Tooltip id={`shortcut-manager-pin-${tab.shortcutKey}`} place="top" style={{ zIndex: 70 }} />
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </div>
        </HoverRevealScrollbarWrapper>
      </div>
    </div>
  );
};

export default ShortcutManagerPopup;
